import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useView } from '@hooks';
import { Tabs, TabsProps } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import './SharedTabs.css';

export interface ISharedTabsProps extends TabsProps {
  wrapperClassName?: string;
  wrapperStyle?: React.CSSProperties;
}

export const SharedTabs: React.FC<ISharedTabsProps> = ({
  wrapperClassName,
  wrapperStyle,
  className,
  more,
  items,
  ...rest
}) => {
  const { isMobile } = useView();
  const navRef = useRef<HTMLDivElement>(null);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);

  const getNavWrap = () =>
    navRef.current?.querySelector('.ant-tabs-nav-wrap') as HTMLElement | null;

  useEffect(() => {
    if (!isMobile) return;
    const el = getNavWrap();
    if (!el) return;

    const updateScrollState = () => {
      setCanScrollLeft(el.classList.contains('ant-tabs-nav-wrap-ping-left'));
      setCanScrollRight(el.classList.contains('ant-tabs-nav-wrap-ping-right'));
    };

    updateScrollState();

    const observer = new MutationObserver(updateScrollState);
    observer.observe(el, { attributes: true, attributeFilter: ['class'] });
    window.addEventListener('resize', updateScrollState);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateScrollState);
    };
  }, [isMobile, items?.length]);

  const scrollByTabs = (direction: 'left' | 'right') => {
    const el = getNavWrap();
    if (!el) return;
    const firstTab = el.querySelector('.ant-tabs-tab') as HTMLElement | null;
    const tabWidth = firstTab?.offsetWidth ?? 100;
    const totalAmount = tabWidth * 3 * (direction === 'right' ? 1 : -1);

    const steps = 15;
    const stepAmount = totalAmount / steps;
    let i = 0;

    const tick = () => {
      if (i >= steps) return;
      el.dispatchEvent(
        new WheelEvent('wheel', {
          deltaX: stepAmount,
          bubbles: true,
          cancelable: true,
        })
      );
      i += 1;
      requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  };

  return (
    <div
      ref={navRef}
      className={`${isMobile ? 'shared-tabs-scroll-wrapper' : ''}${wrapperClassName ? ` ${wrapperClassName}` : ''}`}
      style={wrapperStyle}
    >
      {isMobile && canScrollLeft && (
        <button
          type="button"
          className="shared-tabs-arrow shared-tabs-arrow-left"
          aria-label="Previous tabs"
          onClick={() => scrollByTabs('left')}
        >
          <LeftOutlined />
        </button>
      )}
      {isMobile && canScrollRight && (
        <button
          type="button"
          className="shared-tabs-arrow shared-tabs-arrow-right"
          aria-label="Next tabs"
          onClick={() => scrollByTabs('right')}
        >
          <RightOutlined />
        </button>
      )}
      <Tabs
        {...rest}
        items={items}
        className={`${className || ''}${isMobile ? ' shared-tabs-mobile' : ''}`}
        more={isMobile ? { icon: null } : more}
      />
    </div>
  );
};
